import React from 'react';
import { Quote, Star } from 'lucide-react';
import { useLang } from '@/lib/LangContext';

/*
 * Pet avatar portraits sourced from Pexels (CC0). Order matches the
 * i18n.testimonials.items array:
 *   0 → British shorthair cat (Nino / Luna)
 *   1 → Labrador (Dato / Rex)
 *   2 → Persian / long-hair cat (Mariam / Rich-i)
 *   3 → Golden retriever (Giorgi / Barry)
 * Keep these as portraits that work in a round 56-72px avatar frame.
 */
const AVATARS = [
  'https://images.pexels.com/photos/45201/kitty-cat-kitten-pet-45201.jpeg?auto=compress&cs=tinysrgb&w=240&h=240&fit=crop',
  'https://images.pexels.com/photos/1108099/pexels-photo-1108099.jpeg?auto=compress&cs=tinysrgb&w=240&h=240&fit=crop',
  'https://images.pexels.com/photos/2071882/pexels-photo-2071882.jpeg?auto=compress&cs=tinysrgb&w=240&h=240&fit=crop',
  'https://images.pexels.com/photos/1805164/pexels-photo-1805164.jpeg?auto=compress&cs=tinysrgb&w=240&h=240&fit=crop',
];

/**
 * TestimonialsStrip — a short four-card row that sits between the "Our
 * Experts" block and the Contacts section. Builds trust just before the
 * contact CTA. Content comes from i18n.testimonials.* so it stays bilingual.
 * Placeholder quotes for now — swap once real member stories come in.
 */
export default function TestimonialsStrip() {
  const { t } = useLang();
  const ts = t.testimonials;
  if (!ts || !Array.isArray(ts.items) || ts.items.length === 0) return null;

  return (
    <section
      id="testimonials"
      data-testid="testimonials-strip"
      className="relative py-12 md:py-16 bg-[#FDFBF7]"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="max-w-2xl mx-auto text-center">
          {ts.eyebrow && (
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#0A4D8C]/20 text-[11px] font-bold uppercase tracking-wider text-[#0A4D8C]"
              data-testid="testimonials-eyebrow"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#0A4D8C]" />
              {ts.eyebrow}
            </div>
          )}
          <h2
            data-testid="testimonials-title"
            className="mt-3 font-display font-extrabold text-[#05223D] text-2xl md:text-3xl lg:text-[34px] tracking-[-0.02em] leading-[1.15]"
          >
            {ts.title}
          </h2>
          {ts.subtitle && (
            <p className="mt-3 text-[#465B70] text-sm md:text-base max-w-xl mx-auto leading-relaxed">
              {ts.subtitle}
            </p>
          )}
        </div>

        <div
          className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5"
          role="list"
        >
          {ts.items.slice(0, 4).map((item, i) => (
            <article
              key={i}
              role="listitem"
              data-testid={`testimonial-card-${i}`}
              className="group relative flex flex-col h-full bg-white rounded-2xl border border-[#eadfce] p-5 md:p-6 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(10,77,140,0.1)] transition-all duration-300"
            >
              <Quote
                className="absolute top-4 right-4 w-5 h-5 text-[#F25C05]/35 shrink-0"
                aria-hidden
              />

              <div className="flex items-center gap-1 text-[#F25C05]" aria-label="5 out of 5 stars">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} className="w-3.5 h-3.5 fill-current" aria-hidden />
                ))}
              </div>

              <p
                className="mt-3 text-[13.5px] md:text-sm text-[#334155] leading-relaxed flex-1"
                data-testid={`testimonial-quote-${i}`}
              >
                “{item.quote}”
              </p>

              <div className="mt-5 pt-4 border-t border-[#eadfce] flex items-center gap-3">
                <img
                  src={item.avatar_url || AVATARS[i] || AVATARS[0]}
                  alt={item.pet || item.name}
                  loading="lazy"
                  className="w-11 h-11 rounded-full object-cover border-2 border-white shadow-[0_4px_12px_rgba(10,77,140,0.15)] shrink-0"
                />
                <div className="min-w-0">
                  <p
                    className="font-display font-bold text-[#05223D] text-[13px] md:text-sm leading-tight truncate"
                    data-testid={`testimonial-name-${i}`}
                  >
                    {item.name}
                  </p>
                  <p className="text-[11px] text-[#465B70] leading-tight mt-0.5 truncate">
                    {item.city}
                  </p>
                  {item.pet && (
                    <p className="text-[10.5px] text-[#F25C05] font-semibold uppercase tracking-wider leading-tight mt-1 truncate">
                      {item.pet}
                    </p>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
